import { motion } from "framer-motion";
import { ParticleField } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-semibold text-[#F8FAFC] mb-4">{title}</h2>
    <div className="text-sm text-[#94A3B8] leading-relaxed space-y-3">{children}</div>
  </div>
);

export const TermsPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Terms of Service"
        description="ProcureAide's terms of service. Read our terms and conditions for using the platform."
        path="/terms"
        noindex
      />
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-20" />
        <ParticleField count={12} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4">Legal</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] tracking-tight mb-4">
              Terms & Conditions
            </h1>
            <p className="text-[#64748B] text-sm mb-12">
              Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="bg-[#0B1426]/60 border border-[#00B4D8]/10 rounded-2xl p-8 sm:p-12"
            data-testid="terms-content"
          >
            <Section title="1. Introduction">
              <p>Welcome to Procureaide ("Platform", "Service", "we", "us", or "our"). These Terms and Conditions ("Terms") govern your access to and use of the Procureaide website located at procureaide.co, operated by Procureaide Pvt Ltd, a company registered in Pakistan.</p>
              <p>By accessing or using our Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Platform.</p>
            </Section>

            <Section title="2. Definitions">
              <p><strong className="text-[#F8FAFC]">"Platform"</strong> refers to the Procureaide website, software applications, and all related services.</p>
              <p><strong className="text-[#F8FAFC]">"Products"</strong> refers to DataRefinery, SpendLens, and ConTrack — the modular procurement intelligence tools offered by Procureaide.</p>
              <p><strong className="text-[#F8FAFC]">"User"</strong> refers to any individual or entity that accesses or uses the Platform.</p>
              <p><strong className="text-[#F8FAFC]">"Content"</strong> refers to all information, data, text, software, graphics, and other materials available through the Platform.</p>
            </Section>

            <Section title="3. Use of the Platform">
              <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You shall not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Platform in any way that violates any applicable national or international law or regulation.</li>
                <li>Attempt to gain unauthorized access to any part of the Platform, other accounts, computer systems, or networks connected to the Platform.</li>
                <li>Use the Platform to transmit any harmful, offensive, or unlawful content.</li>
                <li>Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Platform without express written permission.</li>
                <li>Use automated systems (bots, scrapers, crawlers) to access the Platform without prior written consent.</li>
              </ul>
            </Section>

            <Section title="4. Products and Services">
              <p>Procureaide offers three modular AI-powered procurement intelligence products: DataRefinery, SpendLens, and ConTrack. Each product can be purchased independently or in combination.</p>
              <p>We reserve the right to modify, suspend, or discontinue any Product or feature at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Products.</p>
              <p>Product demonstrations, walkthroughs, and screenshots displayed on the Platform are for illustrative purposes and may not reflect the exact current state of the Products.</p>
            </Section>

            <Section title="5. Intellectual Property">
              <p>All content, features, and functionality of the Platform — including but not limited to text, graphics, logos, icons, images, software, and the underlying technology — are the exclusive property of Procureaide Pvt Ltd and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
              <p>The Procureaide name, logo, and all related product names, design marks, and slogans are trademarks of Procureaide Pvt Ltd. You may not use these marks without our prior written permission.</p>
            </Section>

            <Section title="6. User Data and Privacy">
              <p>Your use of the Platform is also governed by our Privacy Policy. By using the Platform, you consent to the collection and use of your information as described in the Privacy Policy.</p>
              <p>When using our Products, you may upload or input procurement data including but not limited to spend data, contract documents, and supplier information. You retain ownership of your data. We process your data solely to provide the Services and as outlined in our Privacy Policy.</p>
            </Section>

            <Section title="7. Demo Requests and Communications">
              <p>By submitting a demo request or contacting us through the Platform, you consent to receive communications from Procureaide related to your inquiry. You may opt out of marketing communications at any time by contacting us at info@procureaide.com.</p>
            </Section>

            <Section title="8. Newsletter Subscriptions">
              <p>By subscribing to our newsletter, you consent to receive periodic emails about procurement intelligence, industry news, product updates, and related content. You may unsubscribe at any time by contacting us at info@procureaide.com or using the unsubscribe mechanism provided in each email.</p>
            </Section>

            <Section title="9. Limitation of Liability">
              <p>To the maximum extent permitted by applicable law, Procureaide Pvt Ltd and its directors, employees, partners, agents, suppliers, or affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of, or inability to access or use, the Platform.</li>
                <li>Any conduct or content of any third party on the Platform.</li>
                <li>Any content obtained from the Platform.</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
              </ul>
            </Section>

            <Section title="10. Disclaimer of Warranties">
              <p>The Platform is provided on an "AS IS" and "AS AVAILABLE" basis without any warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</p>
              <p>Procureaide does not warrant that the Platform will be uninterrupted, secure, or error-free, or that any defects will be corrected.</p>
            </Section>

            <Section title="11. Indemnification">
              <p>You agree to defend, indemnify, and hold harmless Procureaide Pvt Ltd and its officers, directors, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt arising from your use of and access to the Platform, or your violation of these Terms.</p>
            </Section>

            <Section title="12. Governing Law">
              <p>These Terms shall be governed by and construed in accordance with the laws of Pakistan, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Pakistan.</p>
            </Section>

            <Section title="13. Changes to Terms">
              <p>We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
              <p>By continuing to access or use our Platform after those revisions become effective, you agree to be bound by the revised Terms.</p>
            </Section>

            <Section title="14. Severability">
              <p>If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.</p>
            </Section>

            <Section title="15. Contact Us">
              <p>If you have any questions about these Terms, please contact us:</p>
              <ul className="list-none space-y-1">
                <li><strong className="text-[#F8FAFC]">Procureaide Pvt Ltd</strong></li>
                <li>Email: <a href="mailto:info@procureaide.com" className="text-[#00B4D8] hover:underline">info@procureaide.com</a></li>
                <li>Website: <a href="https://procureaide.co" className="text-[#00B4D8] hover:underline">procureaide.co</a></li>
              </ul>
            </Section>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
